/**
 * Service Container Models
 * 
 * Simple DI container for Kendo widgets. Services can be:
 * - Singleton: one instance shared everywhere
 * - Scoped: new instance created per resolution
 */

export enum ServiceLifetime {
    Singleton = "singleton",
    Scoped = "scoped"
}

/** Constructor type for class-based injection */
export type Constructor<T = any> = new (...args: any[]) => T;

export interface IServiceRegistration<T = any> {
    /** The class constructor (used as token) */
    ctor: Constructor<T>;
    /** Factory to create the service */
    factory: () => T;
    /** Service lifetime */
    lifetime: ServiceLifetime;
    /** Cached instance (for singletons) */
    instance?: T;
}

export interface IServiceContainer {
    /**
     * Register a class as a singleton service.
     */
    singleton<T>(ctor: Constructor<T>, factory?: () => T): void;

    /**
     * Register an existing instance as a singleton.
     */
    singletonInstance<T>(ctor: Constructor<T>, instance: T): void;

    /**
     * Register a class as a scoped service.
     */
    scoped<T>(ctor: Constructor<T>, factory?: () => T): void;

    /**
     * Resolve a service by its class constructor.
     */
    resolve<T>(ctor: Constructor<T>): T;

    /**
     * Try to resolve a service, returns undefined if not registered.
     */
    tryResolve<T>(ctor: Constructor<T>): T | undefined;

    /**
     * Check if a service is registered.
     */
    has<T>(ctor: Constructor<T>): boolean;

    /**
     * Get the lifetime of a registered service.
     */
    getLifetime<T>(ctor: Constructor<T>): ServiceLifetime | undefined;
}
